import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Unity, useUnityContext } from 'react-unity-webgl';
import { AppConfig, GameInitParams, UnityGameState, UnityGameEvent, UnityGameError, UnityPlayer } from '../types';
import { emit as emitTelemetry, forwardFromUnity, newCorrelationId, nowMs } from '../services/telemetry/FlexoTelemetry';
import { flexoBridge, installFlexoBridge } from '../services/unity/flexoBridgeSignalR';
import { unityPreloader } from '../services/unityPreloader';
import { LoadingExperience } from './LoadingExperience';
import { devLog } from '@gamedate/core-config';
import { RoomJoinError, RoomJoinErrorState } from './RoomJoinError';
import { parseAppearanceData, serializeAppearanceData } from '@gamedate/core-character/appearance';
import { GESTURE_MAP, GestureDef } from '@gamedate/core-character/gestures';
import { useCharacter } from '../context/CharacterContext';
import { EmoteBar } from './EmoteBar/EmoteBar';
import { ControlsPanel } from './ControlsPanel';
import { TouchActions, TouchStick, RotateToLandscape, TouchCoach, type MoveState } from './TouchActions';
import { initialDeviceTier, measureDeviceTier, type DeviceTier } from '../lib/deviceTier';
import { isMobileDevice } from '../hooks';
import './UnityGame.css';

type UnityEventParameter = string | number | undefined;

// FlexoRoom message envelope (mirrors FlexoCore.Bridge.FlexoMessage on the Unity side).
interface FlexoMessage {
  type: string;
  data: string;
  correlationId: string;
  timestamp: number;
}

interface FlexoInboundEvent {
  type: string;
  data: string;
  correlationId?: string;
  timestamp?: number;
}

const HANDSHAKE_TIMEOUT_MS = 3000;
const HANDSHAKE_MAX_ATTEMPTS = 5;

// Context-menu actions per interactable kind (ids are gesture-catalog ids — the
// animations ship in the Unity build; Unity handles "sit-chair" specially by
// aligning the body onto the seat before playing it).
const INTERACT_ACTIONS: Record<string, { id: string; label: string; emoji: string }[]> = {
  seat: [
    { id: 'sit-chair', label: 'Sit', emoji: '🪑' },
    { id: 'pick-up', label: 'Pick up', emoji: '🫳' },
    { id: 'put-down', label: 'Put down', emoji: '📦' },
    { id: 'throw', label: 'Throw', emoji: '🎯' },
  ],
  button: [{ id: 'push-button', label: 'Push', emoji: '🔘' }],
};

// In-room emote bar. Buttons come from the player's gesture loadout — the gestures
// they picked in the character builder (stored inside appearanceData; defaults to the
// classic five + favorites via parseAppearanceData). Each button sends an `emote`
// message to Unity's FlexoReactBridge; FlexoRoomController.TriggerEmote plays it
// locally and networks it to other players. Ids must be gesture-catalog ids.

interface UnityGameProps {
  config: AppConfig;
  onLoaded?: () => void;
  gameParams?: GameInitParams;
  onUnityReady?: () => void;
  onGameStateChange?: (state: UnityGameState) => void;
  onGameEvent?: (event: UnityGameEvent) => void;
  onGameError?: (error: UnityGameError) => void;
  onPlayersUpdate?: (players: UnityPlayer[]) => void;
  /** Player tapped a remote avatar in-world (playerId = user id). */
  onOpenProfile?: (playerId: string, displayName?: string) => void;
  /** Vicinity scanner set changed — ids of remote players within earshot. */
  onNearbyPlayers?: (ids: string[]) => void;
}

export function UnityGame({
  config,
  onLoaded,
  gameParams,
  onUnityReady,
  onGameStateChange,
  onGameEvent,
  onGameError,
  onPlayersUpdate,
  onOpenProfile,
  onNearbyPlayers,
}: UnityGameProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isUnityReady, setIsUnityReady] = useState(false);
  const [gameState, setGameState] = useState<UnityGameState>('waiting_for_params');
  // The body's current verb, so the thumb buttons can say what they will actually do.
  const [moveState, setMoveState] = useState<MoveState>('ground');
  const gameParamsSentRef = useRef(false);
  // Live room roster aggregated from Unity playerJoined/playerLeft messages.
  const rosterRef = useRef(new Map<string, UnityPlayer>());
  const localPlayerIdRef = useRef<string | undefined>(undefined);
  useEffect(() => { localPlayerIdRef.current = gameParams?.playerId; }, [gameParams?.playerId]);

  const {
    unityProvider,
    isLoaded,
    loadingProgression,
    sendMessage,
    addEventListener,
    removeEventListener,
    requestFullscreen,
    unload,
  } = useUnityContext({
    loaderUrl: config.unity.loaderUrl,
    dataUrl: config.unity.dataUrl,
    frameworkUrl: config.unity.frameworkUrl,
    codeUrl: config.unity.codeUrl,
    streamingAssetsUrl: config.unity.streamingAssetsUrl,
    companyName: config.unity.companyName,
    productName: config.unity.productName,
    productVersion: config.unity.productVersion,
    autoSyncPersistentDataPath: true,
  });

  // Track if we've sent the app mode
  const modeSentRef = useRef(false);

  // Give the SignalR interop bridge (window.FlexoBridge) a way to call back
  // into Unity. Installed at module load so it exists before the WASM boots;
  // attached here so it uses this Unity instance's sendMessage.
  useEffect(() => {
    installFlexoBridge();
    flexoBridge.attachUnity(sendMessage);
    return () => {
      flexoBridge.detachUnity();
    };
  }, [sendMessage]);

  // Unity is about to fetch the build files itself — abort any background
  // preload NOW so the two never stream the same ~53 MB concurrently (a
  // measured cold-room load downloaded the build twice without this).
  useEffect(() => {
    unityPreloader.stopPreloading();
  }, []);

  // Handle Unity loaded event
  useEffect(() => {
    if (isLoaded) {
      onLoaded?.();
      // Unity has the data file in its IndexedDB cache now — stamp its hash so
      // RoomJoin's warm fetch skips re-downloading it on future visits.
      const hash = new URL(config.unity.dataUrl).searchParams.get('v');
      if (hash) localStorage.setItem('gamedate_unity_data_v', hash);
    }
  }, [isLoaded, onLoaded, config.unity.dataUrl]);

  // Listen for Unity router ready event and send game mode.
  // unity-router-ready can fire during boot before isLoaded — sending then is
  // silently swallowed by react-unity-webgl, so defer to the isLoaded fallback.
  const isLoadedForModeRef = useRef(false);
  useEffect(() => {
    const handleRouterReady = () => {
      if (!isLoadedForModeRef.current) {
        devLog('[UnityGame] Router ready before Unity instantiated — deferring to isLoaded fallback');
        return;
      }
      if (!modeSentRef.current) {
        devLog('[UnityGame] Router ready - sending game mode');
        sendMessage('UnifiedSceneRouter', 'SetAppMode', 'game');
        modeSentRef.current = true;
      }
    };

    window.addEventListener('unity-router-ready', handleRouterReady);
    return () => {
      window.removeEventListener('unity-router-ready', handleRouterReady);
    };
  }, [sendMessage]);

  // Also try to send mode when Unity is loaded (fallback)
  useEffect(() => {
    isLoadedForModeRef.current = isLoaded;
    if (isLoaded && !modeSentRef.current) {
      const timeoutId = setTimeout(() => {
        if (!modeSentRef.current) {
          devLog('[UnityGame] Unity loaded - sending game mode (fallback)');
          sendMessage('UnifiedSceneRouter', 'SetAppMode', 'game');
          modeSentRef.current = true;
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoaded, sendMessage]);

  // Helper function to send messages to FlexoReactBridge.
  // Stamps every outbound envelope with a correlationId + timestamp so the
  // AC 7.1 event trail can be reconstructed end-to-end. Returns the
  // correlationId so callers can chain it through SignalR invocations.
  const sendFlexoMessage = useCallback((type: string, data: unknown, correlationId?: string): string => {
    const cid = correlationId ?? newCorrelationId();
    const message: FlexoMessage = {
      type,
      data: typeof data === 'string' ? data : JSON.stringify(data),
      correlationId: cid,
      timestamp: nowMs(),
    };
    devLog('[UnityGame] Sending FlexoRoom message:', type, cid, data);
    sendMessage('FlexoReactBridge', 'ReceiveMessage', JSON.stringify(message));
    return cid;
  }, [sendMessage]);

  // Keyboard focus guard: while any web text field has focus (chat, DMs,
  // forms), tell Unity to ignore movement keys and emote hotkeys — typing
  // "wasd" must write letters, not walk the character. focusin/focusout
  // bubble to document; the state is read on the next tick because focusout
  // fires while focus is still in transit between elements.
  const typingModeSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isUnityReady) return;

    const isTextEntry = (el: Element | null): boolean => {
      if (!el) return false;
      if (el instanceof HTMLTextAreaElement) return true;
      if (el instanceof HTMLInputElement) {
        return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'color', 'file'].includes(el.type);
      }
      return el instanceof HTMLElement && el.isContentEditable;
    };

    const send = () => {
      const value = isTextEntry(document.activeElement) ? '1' : '0';
      if (typingModeSentRef.current !== value) {
        typingModeSentRef.current = value;
        sendFlexoMessage('typing_mode', value);
      }
    };
    const onFocusChange = () => setTimeout(send, 0);

    document.addEventListener('focusin', onFocusChange);
    document.addEventListener('focusout', onFocusChange);
    send(); // sync current state — the user may already be typing when Unity readies

    return () => {
      document.removeEventListener('focusin', onFocusChange);
      document.removeEventListener('focusout', onFocusChange);
      // Best-effort release so a dying UI never leaves the game input-locked.
      if (typingModeSentRef.current === '1') {
        typingModeSentRef.current = null;
        sendFlexoMessage('typing_mode', '0');
      }
    };
  }, [isUnityReady, sendFlexoMessage]);

  // Bridge handshake: when Unity emits its ready signal, send a typed `ping`
  // and require a matching `pong`. A single 3s window is too tight on slow
  // devices / cold caches — Unity's first scene frame can lag isLoaded by many
  // seconds and pings sent before FlexoReactBridge exists are dropped. Retry
  // up to HANDSHAKE_MAX_ATTEMPTS pings (each with its own timeout) before
  // surfacing a typed handshake_failed error (AC 1.4).
  const pendingPingRef = useRef<{ correlationId: string; timeoutId: ReturnType<typeof setTimeout> } | null>(null);
  const [handshakeError, setHandshakeError] = useState<RoomJoinErrorState | null>(null);
  useEffect(() => {
    if (!isLoaded || isUnityReady || pendingPingRef.current) return;
    if (handshakeError) return;

    let cancelled = false;
    let attempt = 0;

    const tryPing = () => {
      if (cancelled || isUnityReadyRef.current) return;
      attempt += 1;
      const cid = newCorrelationId();
      const timeoutId = setTimeout(() => {
        pendingPingRef.current = null;
        if (cancelled || isUnityReadyRef.current) return;
        if (attempt < HANDSHAKE_MAX_ATTEMPTS) {
          console.warn(`[UnityGame] Ping ${attempt}/${HANDSHAKE_MAX_ATTEMPTS} unanswered, retrying`);
          tryPing();
          return;
        }
        console.error('[UnityGame] Bridge handshake timeout');
        emitTelemetry({
          event: 'bridge_handshake_failed',
          roomId: gameParams?.roomId ?? null,
          playerId: gameParams?.playerId ?? null,
          correlationId: cid,
          ts: nowMs(),
          payload: { reason: 'pong_timeout', attempts: attempt },
        });
        setHandshakeError({
          code: 'handshake_timeout',
          message: 'Unity bridge did not respond',
          correlationId: cid,
        });
        onGameError?.({ code: 'handshake_timeout', message: 'Unity bridge did not respond' });
      }, HANDSHAKE_TIMEOUT_MS);
      pendingPingRef.current = { correlationId: cid, timeoutId };
      sendFlexoMessage('ping', { ts: nowMs() }, cid);
    };

    tryPing();

    return () => {
      cancelled = true;
      if (pendingPingRef.current) {
        clearTimeout(pendingPingRef.current.timeoutId);
        pendingPingRef.current = null;
      }
    };
  }, [isLoaded, isUnityReady, handshakeError, sendFlexoMessage, gameParams?.roomId, gameParams?.playerId, onGameError]);

  const handleHandshakeRetry = useCallback(() => {
    setHandshakeError(null);
  }, []);

  // Listen for Unity bridge events (both legacy and FlexoRoom)
  const isUnityReadyRef = useRef(false);
  useEffect(() => {
    isUnityReadyRef.current = isUnityReady;
  }, [isUnityReady]);

  // Owned items → Unity (Cove P6): SocialPlayPanel broadcasts the server-authoritative
  // inventory as a window event; Unity's key-gated doors (Duo Key) read the relayed set.
  //
  // Flight tiers ride the same set (Unity gates them via HasOwnedItem). On the dating
  // site the glider and jetpack are simply part of the playground; superman (fast free
  // flight) stays behind a debug query flag. Unity CLEARS the set on every ownedItems
  // message, so the flight grants must be merged into every send, not sent once.
  const flightItems = useCallback((): string[] => {
    const items = ['glider', 'jetpack'];
    try {
      if (new URLSearchParams(window.location.search).has('superfly')) items.push('superman');
    } catch { /* ignore */ }
    return items;
  }, []);
  useEffect(() => {
    const relayOwnedItems = (e: Event) => {
      const detail = (e as CustomEvent<{ items: string[] }>).detail;
      if (!detail || !isUnityReadyRef.current) return;
      sendFlexoMessage('ownedItems', { items: [...detail.items, ...flightItems()] });
    };
    window.addEventListener('flexo-owned-items', relayOwnedItems);
    return () => window.removeEventListener('flexo-owned-items', relayOwnedItems);
  }, [sendFlexoMessage, flightItems]);
  // Baseline grant so flight works before (or without) any inventory broadcast.
  useEffect(() => {
    if (!isUnityReady) return;
    sendFlexoMessage('ownedItems', { items: flightItems() });
  }, [isUnityReady, sendFlexoMessage, flightItems]);

  // WHAT THIS DEVICE CAN AFFORD. The guess goes over as soon as the bridge is up (so the very
  // first frame is already budgeted), then the frame rate we actually get corrects it once and is
  // remembered for next time — a phone is not a promise, it is a measurement. See lib/deviceTier.
  useEffect(() => {
    if (!isUnityReady) return undefined;
    const start: DeviceTier = initialDeviceTier();
    sendFlexoMessage('quality_tier', { tier: start });
    return measureDeviceTier(start, (corrected) => {
      sendFlexoMessage('quality_tier', { tier: corrected });
    });
  }, [isUnityReady, sendFlexoMessage]);
  useEffect(() => {
    const handleUnityReady = () => {
      // One-shot guard: legacy `unityReady` and a delayed `pong` can both
      // arrive in the same handshake; we must only fire onUnityReady once.
      if (isUnityReadyRef.current) return;
      devLog('[UnityGame] Unity ready event received');
      isUnityReadyRef.current = true;
      setIsUnityReady(true);
      onUnityReady?.();
    };

    // Handle FlexoRoom messages from Unity (via unityMessage CustomEvent)
    const handleFlexoMessage = (e: CustomEvent<FlexoInboundEvent>) => {
      const { type, data, correlationId } = e.detail;
      devLog('[UnityGame] FlexoRoom message:', type, data, correlationId);

      switch (type) {
        case 'pong': {
          if (pendingPingRef.current && pendingPingRef.current.correlationId === correlationId) {
            clearTimeout(pendingPingRef.current.timeoutId);
            pendingPingRef.current = null;
            emitTelemetry({
              event: 'bridge_handshake_ok',
              roomId: gameParams?.roomId ?? null,
              playerId: gameParams?.playerId ?? null,
              correlationId: correlationId ?? '',
              ts: nowMs(),
            });
            handleUnityReady();
          }
          break;
        }
        case 'telemetry': {
          try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            forwardFromUnity(parsed);
          } catch (err) {
            console.warn('[UnityGame] Failed to forward Unity telemetry:', err);
          }
          break;
        }
        case 'unityReady':
          // Legacy ready signal — only honour it if the typed handshake hasn't
          // been initiated yet, so production builds enforce ping/pong.
          if (!pendingPingRef.current) {
            handleUnityReady();
          }
          break;
        case 'stateChange': {
          const stateData = typeof data === 'string' ? JSON.parse(data) : data;
          const state = stateData?.state as UnityGameState;
          if (state) {
            setGameState(state);
            onGameStateChange?.(state);
          }
          break;
        }
        case 'error': {
          const errorData = typeof data === 'string' ? JSON.parse(data) : data;
          const error: UnityGameError = {
            code: errorData?.errorType || 'unknown',
            message: errorData?.message || 'Unknown error',
          };
          console.error('[UnityGame] FlexoRoom error:', error);
          onGameError?.(error);
          break;
        }
        case 'move_state': {
          try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsed?.state) setMoveState(String(parsed.state) as MoveState);
          } catch {
            /* a malformed state frame just leaves the buttons as they were */
          }
          break;
        }
        case 'openProfile': {
          try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsed?.playerId) {
              onOpenProfile?.(String(parsed.playerId), parsed.displayName || undefined);
            }
          } catch (err) {
            console.warn('[UnityGame] Bad openProfile payload:', err);
          }
          break;
        }
        case 'nearbyPlayers': {
          try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (Array.isArray(parsed?.ids)) {
              onNearbyPlayers?.(parsed.ids.map(String));
            }
          } catch (err) {
            console.warn('[UnityGame] Bad nearbyPlayers payload:', err);
          }
          break;
        }
        // Nearest interactable prop changed (or cleared — empty payload).
        case 'interactable': {
          try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            setInteractable(parsed?.kind ? { kind: String(parsed.kind), name: String(parsed.name ?? '') } : null);
          } catch {
            setInteractable(null);
          }
          break;
        }
        // Live roster: aggregate joins/leaves into a map and surface via
        // onPlayersUpdate (drives the private-talk target picker and any
        // roster UI). The legacy 'unity-event' forward is kept as-is.
        case 'playerJoined': {
          try {
            // data arrives as a JSON string from some bridge paths and as an
            // already-parsed object from others — tolerate both.
            const p = typeof data === 'string' ? JSON.parse(data || '{}') : ((data as Record<string, unknown>) ?? {});
            const id = String(p.playerId ?? p.userId ?? p.id ?? '');
            if (id) {
              rosterRef.current.set(id, {
                playerId: id,
                playerName: p.displayName ?? p.playerName ?? 'Player',
                slotIndex: rosterRef.current.size,
                isLocal: id === localPlayerIdRef.current,
              });
              onPlayersUpdate?.([...rosterRef.current.values()]);
            }
          } catch { /* roster is best-effort */ }
          window.dispatchEvent(new CustomEvent('unity-event', {
            detail: { eventName: type, data }
          }));
          break;
        }
        case 'playerLeft': {
          try {
            const p = typeof data === 'string' ? JSON.parse(data || '{}') : ((data as Record<string, unknown>) ?? {});
            const id = String(p.playerId ?? p.userId ?? p.id ?? '');
            if (id && rosterRef.current.delete(id)) {
              onPlayersUpdate?.([...rosterRef.current.values()]);
            }
          } catch { /* roster is best-effort */ }
          window.dispatchEvent(new CustomEvent('unity-event', {
            detail: { eventName: type, data }
          }));
          break;
        }
        case 'roomState':
          // Forward to legacy event system for compatibility
          window.dispatchEvent(new CustomEvent('unity-event', {
            detail: { eventName: type, data }
          }));
          break;
        case 'rtsMode': {
          // The RTS took over (or released) the room — hide/show social chrome.
          try {
            const p = typeof data === 'string' ? JSON.parse(data || '{}') : ((data as Record<string, unknown>) ?? {});
            setRtsMode(!!p.active);
          } catch { /* default stays */ }
          window.dispatchEvent(new CustomEvent('unity-event', {
            detail: { eventName: type, data }
          }));
          break;
        }
        default:
          // Forward unknown events
          window.dispatchEvent(new CustomEvent('unity-event', {
            detail: { eventName: type, data }
          }));
      }
    };

    const handleStateChange = (e: CustomEvent<{ state: UnityGameState }>) => {
      devLog('[UnityGame] State change:', e.detail.state);
      setGameState(e.detail.state);
      onGameStateChange?.(e.detail.state);
    };

    const handleGameEvent = (e: CustomEvent<UnityGameEvent>) => {
      devLog('[UnityGame] Game event:', e.detail.eventName, e.detail.data);
      onGameEvent?.(e.detail);

      // Also dispatch on window for backward compatibility
      window.dispatchEvent(new CustomEvent('unity-event', {
        detail: e.detail
      }));
    };

    const handleGameError = (e: CustomEvent<UnityGameError>) => {
      console.error('[UnityGame] Game error:', e.detail.code, e.detail.message);
      onGameError?.(e.detail);
    };

    const handlePlayersUpdate = (e: CustomEvent<{ players: UnityPlayer[] }>) => {
      devLog('[UnityGame] Players update:', e.detail.players);
      onPlayersUpdate?.(e.detail.players);
    };

    // FlexoRoom events
    window.addEventListener('unityMessage', handleFlexoMessage as EventListener);

    // Legacy events (for backward compatibility)
    window.addEventListener('unity-ready', handleUnityReady);
    window.addEventListener('unity-state-change', handleStateChange as EventListener);
    window.addEventListener('unity-game-event', handleGameEvent as EventListener);
    window.addEventListener('unity-game-error', handleGameError as EventListener);
    window.addEventListener('unity-players-update', handlePlayersUpdate as EventListener);

    return () => {
      window.removeEventListener('unityMessage', handleFlexoMessage as EventListener);
      window.removeEventListener('unity-ready', handleUnityReady);
      window.removeEventListener('unity-state-change', handleStateChange as EventListener);
      window.removeEventListener('unity-game-event', handleGameEvent as EventListener);
      window.removeEventListener('unity-game-error', handleGameError as EventListener);
      window.removeEventListener('unity-players-update', handlePlayersUpdate as EventListener);
    };
  }, [onUnityReady, onGameStateChange, onGameEvent, onGameError, onPlayersUpdate, onOpenProfile, onNearbyPlayers]);

  // The player's gesture loadout: picked in the character builder, stored inside
  // appearanceData. parseAppearanceData sanitizes (known ids, max 8) and falls back
  // to the default loadout when the character predates the gesture picker.
  // loadoutOverride: the user re-picked gestures in the loading-screen mini picker
  // this session — wins over the (now stale) appearanceData snapshot.
  const [loadoutOverride, setLoadoutOverride] = useState<string[] | null>(null);
  const emotes: GestureDef[] = useMemo(() => {
    const loadout = loadoutOverride ?? parseAppearanceData(gameParams?.appearanceData).gestures;
    return loadout
      .map((id) => GESTURE_MAP.get(id))
      .filter((g): g is GestureDef => g !== undefined);
  }, [gameParams?.appearanceData, loadoutOverride]);

  // P1: bare-HUD default on mobile — the emote bar is hidden until the player
  // taps the 👋 FAB or the PlayerSheet's Gesture action, then it auto-collapses
  // after 6 s of inactivity. Desktop keeps it always visible (hotkeys 1-8).
  const isMobile = isMobileDevice();
  const [emoteBarOpen, setEmoteBarOpen] = useState(false);
  // RTS mode (Unity 'rtsMode' bridge event): the battle owns the room — social
  // gestures/emotes are irrelevant there and their bar would collide with the RTS HUD.
  const [rtsMode, setRtsMode] = useState(false);
  // Nearest interactable prop (Unity 'interactable' bridge event) → context action menu.
  const [interactable, setInteractable] = useState<{ kind: string; name: string } | null>(null);
  const emoteCollapseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revealEmoteBar = useCallback(() => {
    setEmoteBarOpen(true);
    if (emoteCollapseRef.current) clearTimeout(emoteCollapseRef.current);
    emoteCollapseRef.current = setTimeout(() => setEmoteBarOpen(false), 6000);
  }, []);
  useEffect(() => {
    const onOpen = () => revealEmoteBar();
    window.addEventListener('gamedate-open-emotebar', onOpen);
    return () => {
      window.removeEventListener('gamedate-open-emotebar', onOpen);
      if (emoteCollapseRef.current) clearTimeout(emoteCollapseRef.current);
    };
  }, [revealEmoteBar]);

  // If the loadout changed AFTER the join message already went out, push the
  // update to Unity so hotkeys 1-8 match the refreshed emote bar.
  useEffect(() => {
    if (loadoutOverride && isUnityReady && gameState === 'in_game') {
      sendFlexoMessage('gesture_loadout', { gestures: loadoutOverride });
    }
  }, [loadoutOverride, isUnityReady, gameState, sendFlexoMessage]);

  // Emote-bar slot picker committed a new loadout: the override updates the bar
  // and (via the effect above) retunes Unity's 1-8 hotkeys; the picked gesture
  // plays once as feedback; and the loadout persists into the active
  // character's appearanceData (the same field the builder writes) so it
  // survives reloads and shows in the builder's Gestures tab.
  const { activeCharacter, updateCharacter } = useCharacter();
  const handleLoadoutChange = useCallback((ids: string[], pickedId: string) => {
    setLoadoutOverride(ids);
    sendFlexoMessage('emote', { animation: pickedId });
    if (activeCharacter) {
      const appearance = parseAppearanceData(activeCharacter.appearanceData);
      appearance.gestures = ids;
      void updateCharacter(activeCharacter.id, {
        appearanceData: serializeAppearanceData(appearance),
      });
    }
  }, [activeCharacter, updateCharacter, sendFlexoMessage]);

  // Send game params when Unity is ready and params are provided
  useEffect(() => {
    if (isLoaded && isUnityReady && gameParams && !gameParamsSentRef.current) {
      devLog('[UnityGame] Sending game params via FlexoReactBridge:', gameParams);

      // No settle delay: the pong handshake already proved FlexoReactBridge is
      // alive, and Unity's bridge queues messages until handlers register — a
      // hard delay here just added dead time to every join (mobile-loading P2 B2;
      // was 500 ms). setToken also starts Unity's SignalR connect (P2 B1), so it
      // must go out as early as possible.
      try {
        // First set auth token if provided
        if (gameParams.accessToken) {
          sendFlexoMessage('setToken', {
            token: gameParams.accessToken,
            userId: gameParams.playerId,
            displayName: gameParams.playerName,
          });
        }

        // Then join the room
        const joinRequest = {
          roomCode: gameParams.roomId,
          playerId: gameParams.playerId,
          displayName: gameParams.playerName,
          characterType: gameParams.characterType || 'default',
          gender: gameParams.gender || 'male',
          appearanceData: gameParams.appearanceData || '',
        };
        devLog('[UnityGame] Sending joinRoom:', joinRequest);
        sendFlexoMessage('joinRoom', joinRequest);

        // Tell Unity which gestures sit on hotkeys 1-8 (same order as the emote bar).
        sendFlexoMessage('gesture_loadout', { gestures: emotes.map((e) => e.id) });
      } catch (e) {
        console.error('[UnityGame] Failed to send FlexoRoom message:', e);
      }

      gameParamsSentRef.current = true;
    }
  }, [isLoaded, isUnityReady, gameParams, sendFlexoMessage, emotes]);

  // Reset the sent flag when gameParams change
  useEffect(() => {
    gameParamsSentRef.current = false;
    rosterRef.current.clear();
  }, [gameParams?.roomId, gameParams?.playerId]);

  // Handle legacy Unity events (for backward compatibility)
  const handleUnityEvent = useCallback((...parameters: UnityEventParameter[]) => {
    const [eventName, data] = parameters;
    devLog('[UnityGame] Legacy Unity Event:', eventName, data);
    window.dispatchEvent(new CustomEvent('unity-event', {
      detail: { eventName, data }
    }));
  }, []);

  useEffect(() => {
    addEventListener('GameEvent', handleUnityEvent as Parameters<typeof addEventListener>[1]);
    return () => {
      removeEventListener('GameEvent', handleUnityEvent as Parameters<typeof removeEventListener>[1]);
    };
  }, [addEventListener, removeEventListener, handleUnityEvent]);

  // Track loaded state in a ref for cleanup
  const isLoadedRef = useRef(false);
  useEffect(() => {
    isLoadedRef.current = isLoaded;
  }, [isLoaded]);

  // Cleanup on unmount - only unload if Unity was successfully loaded
  useEffect(() => {
    return () => {
      if (isLoadedRef.current) {
        unload().catch((err) => {
          // Ignore unload errors during cleanup
          devLog('[UnityGame] Cleanup unload:', err?.message || 'completed');
        });
      }
    };
  }, [unload]);

  const handleFullscreen = () => {
    requestFullscreen(true);
    setIsFullscreen(true);
  };

  // Expose sendMessage and helper functions to window for external use
  useEffect(() => {
    const windowWithUnity = window as Window & {
      sendUnityMessage?: typeof sendMessage;
      sendFlexoMessage?: (type: string, data: unknown) => void;
      joinFlexoRoom?: (params: GameInitParams) => void;
      leaveFlexoRoom?: () => void;
    };

    windowWithUnity.sendUnityMessage = sendMessage;
    windowWithUnity.sendFlexoMessage = sendFlexoMessage;

    // Helper function to join a FlexoRoom
    windowWithUnity.joinFlexoRoom = (params: GameInitParams) => {
      if (isLoaded && isUnityReady) {
        // Set token first
        if (params.accessToken) {
          sendFlexoMessage('setToken', {
            token: params.accessToken,
            userId: params.playerId,
            displayName: params.playerName,
          });
        }
        // Join room
        sendFlexoMessage('joinRoom', {
          roomCode: params.roomId,
          playerId: params.playerId,
          displayName: params.playerName,
          characterType: params.characterType || 'default',
          gender: params.gender || 'male',
          appearanceData: params.appearanceData || '',
        });
      } else {
        console.warn('[UnityGame] Unity not ready yet');
      }
    };

    // Helper function to leave room
    windowWithUnity.leaveFlexoRoom = () => {
      if (isLoaded) {
        sendFlexoMessage('leaveRoom', {});
      }
    };

    return () => {
      delete windowWithUnity.sendUnityMessage;
      delete windowWithUnity.sendFlexoMessage;
      delete windowWithUnity.joinFlexoRoom;
      delete windowWithUnity.leaveFlexoRoom;
    };
  }, [sendMessage, sendFlexoMessage, isLoaded, isUnityReady]);

  const loadingPercentage = Math.round(loadingProgression * 100);

  return (
    <div className="unity-container">
      {!isLoaded && config.features.showLoadingScreen && (
        <div
          className="loading-overlay"
          style={{ backgroundColor: config.theme.backgroundColor }}
        >
          {/* Progress with MB + stage labels, tips carousel, and a WORKING mini
              gesture picker — the download wait doubles as setup time. */}
          <LoadingExperience
            percentage={loadingPercentage}
            appName={config.name}
            primaryColor={config.theme.primaryColor}
            textColor={config.theme.textColor}
            onGesturesSaved={setLoadoutOverride}
          />
        </div>
      )}

      {/* Show connecting state while Unity is connecting */}
      {isLoaded && gameState !== 'in_game' && gameState !== 'waiting_for_params' && (
        <div
          className="loading-overlay"
          style={{ backgroundColor: config.theme.backgroundColor, opacity: 0.9 }}
        >
          <div className="loading-content">
            <h2 style={{ color: config.theme.textColor }}>
              {getStateDisplayText(gameState)}
            </h2>
            {gameState === 'error' && (
              <p style={{ color: config.theme.textColor }}>
                An error occurred. Please try again.
              </p>
            )}
          </div>
        </div>
      )}

      <Unity
        unityProvider={unityProvider}
        className="unity-canvas"
        style={{
          visibility: isLoaded ? 'visible' : 'hidden',
          width: '100%',
          height: '100%',
        }}
      />

      {handshakeError && (
        <div
          className="loading-overlay"
          style={{ backgroundColor: config.theme.backgroundColor, opacity: 0.95 }}
        >
          <RoomJoinError state={handshakeError} onRetry={handleHandshakeRetry} />
        </div>
      )}

      {/* Context actions for the prop the player is standing next to (chair, button…).
          Unity streams the nearest interactable; actions go back as "interact" and play as
          networked emotes (sit additionally aligns the body onto the seat). */}
      {isLoaded && isUnityReady && gameState === 'in_game' && !rtsMode && interactable && (
        <div className="interact-menu" data-kind={interactable.kind}>
          {(INTERACT_ACTIONS[interactable.kind] ?? []).map((a) => (
            <button
              key={a.id}
              className="interact-chip"
              onClick={() => sendFlexoMessage('interact', { action: a.id, target: interactable.name })}
            >
              <span aria-hidden>{a.emoji}</span> {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Left-edge controls reference: the full movement kit, learnable in one place. */}
      {isLoaded && isUnityReady && gameState === 'in_game' && !rtsMode && <ControlsPanel />}

      {/* Touch movement buttons (coarse-pointer devices only — the component gates itself). */}
      {isLoaded && isUnityReady && gameState === 'in_game' && !rtsMode && (
        <>
          <TouchStick send={(x, y) => sendFlexoMessage('move_axis', { x, y })} />
          <TouchActions send={(action) => sendFlexoMessage('move_action', { action })} state={moveState} />
          <TouchCoach />
          <RotateToLandscape />
        </>
      )}

      {/* Gestures are hidden entirely while the RTS owns the room (rtsMode). */}
      {isLoaded && isUnityReady && gameState === 'in_game' && !rtsMode && (!isMobile || emoteBarOpen) && (
        <EmoteBar
          emotes={emotes}
          onEmote={(id) => {
            sendFlexoMessage('emote', { animation: id });
            if (isMobile) revealEmoteBar(); // keep the bar alive while in use
          }}
          onLoadoutChange={handleLoadoutChange}
        />
      )}

      {/* P1: gesture FAB — reveals the emote bar on the bare mobile HUD. */}
      {isLoaded && isUnityReady && gameState === 'in_game' && !rtsMode && isMobile && !emoteBarOpen && (
        <button
          className="emote-fab"
          onClick={revealEmoteBar}
          aria-label="Show gestures"
        >
          👋
        </button>
      )}

      {isLoaded && config.features.showFullscreenButton && !isFullscreen && (
        <button
          className="fullscreen-button"
          onClick={handleFullscreen}
          style={{
            backgroundColor: config.theme.primaryColor,
            color: config.theme.textColor
          }}
          title="Fullscreen"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
          </svg>
        </button>
      )}
    </div>
  );
}

function getStateDisplayText(state: UnityGameState): string {
  switch (state) {
    case 'connecting':
      return 'Connecting to server...';
    case 'joining_room':
      return 'Joining room...';
    case 'loading_scene':
      return 'Loading game...';
    case 'spawning_player':
      return 'Preparing player...';
    case 'disconnected':
      return 'Disconnected';
    case 'error':
      return 'Error';
    default:
      return 'Loading...';
  }
}
